import * as React from 'react'
import { Workflow as FlowArrowIcon } from 'lucide-react'
import type { PlantLineSummary } from '@axilon-apps/domains-ontology-ui-state'
import { PlantHref } from './PlantHref'

/**
 * Site-level FEEDS connectivity diagram. Renders the lines on a site as
 * absolutely-positioned cards in a layered DAG layout, with bezier
 * curves drawn between them per `Line -[:FEEDS]-> Line` edge.
 *
 * The layout uses a longest-path topological rank for the X axis and
 * orders nodes within each layer by the average index of their incoming
 * neighbors in the previous layer, which is a cheap heuristic for
 * minimizing crossings.
 *
 * Click on a node navigates into `LineDetailPage` for that line. The
 * component is read-only otherwise — pan/zoom are not supported; the
 * parent `<div>` should provide horizontal scroll for very wide sites.
 */
export interface SiteFeedsFlowProps {
  siteId: string
  lines: PlantLineSummary[]
  feeds: Array<{ source: string; target: string }>
}

const COL_W = 200
const ROW_H = 84
const NODE_W = 176
const NODE_H = 64
const PAD_X = 16
const PAD_Y = 12

interface LineLayout {
  x: number
  y: number
}

interface LayoutResult {
  width: number
  height: number
  positions: Map<string, LineLayout>
}

function computeLayout(
  lines: PlantLineSummary[],
  feeds: Array<{ source: string; target: string }>
): LayoutResult {
  const lineIds = new Set(lines.map((l) => l.id))
  const edges = feeds.filter((e) => lineIds.has(e.source) && lineIds.has(e.target))

  const outAdj = new Map<string, string[]>()
  const inAdj = new Map<string, string[]>()
  for (const line of lines) {
    outAdj.set(line.id, [])
    inAdj.set(line.id, [])
  }
  for (const edge of edges) {
    outAdj.get(edge.source)?.push(edge.target)
    inAdj.get(edge.target)?.push(edge.source)
  }

  const rank = new Map<string, number>()
  const visiting = new Set<string>()
  const rankOf = (id: string): number => {
    const cached = rank.get(id)
    if (cached !== undefined) return cached
    if (visiting.has(id)) {
      rank.set(id, 0)
      return 0
    }
    visiting.add(id)
    const incoming = inAdj.get(id) ?? []
    let r = 0
    for (const parent of incoming) r = Math.max(r, rankOf(parent) + 1)
    visiting.delete(id)
    rank.set(id, r)
    return r
  }
  for (const line of lines) rankOf(line.id)

  const layers: string[][] = []
  for (const line of lines) {
    const r = rank.get(line.id) ?? 0
    while (layers.length <= r) layers.push([])
    layers[r].push(line.id)
  }
  for (let r = 1; r < layers.length; r++) {
    const prevIdx = new Map(layers[r - 1].map((id, idx) => [id, idx]))
    layers[r].sort((a, b) => avgParentIdx(a, inAdj, prevIdx) - avgParentIdx(b, inAdj, prevIdx))
  }

  const cols = layers.length
  const rows = Math.max(1, ...layers.map((l) => l.length))
  const width = PAD_X * 2 + Math.max(1, cols) * NODE_W + Math.max(0, cols - 1) * (COL_W - NODE_W)
  const height = PAD_Y * 2 + rows * ROW_H

  const positions = new Map<string, LineLayout>()
  for (let c = 0; c < layers.length; c++) {
    const layer = layers[c]
    const totalH = layer.length * ROW_H
    const offsetY = PAD_Y + (height - PAD_Y * 2 - totalH) / 2
    layer.forEach((id, i) => {
      positions.set(id, { x: PAD_X + c * COL_W, y: offsetY + i * ROW_H })
    })
  }
  return { width, height, positions }
}

function avgParentIdx(
  id: string,
  inAdj: Map<string, string[]>,
  prevIdx: Map<string, number>
): number {
  const parents = (inAdj.get(id) ?? []).filter((p) => prevIdx.has(p))
  if (parents.length === 0) return 0
  let sum = 0
  for (const p of parents) sum += prevIdx.get(p) ?? 0
  return sum / parents.length
}

export function SiteFeedsFlow({ siteId, lines, feeds }: SiteFeedsFlowProps): React.ReactElement {
  const layout = React.useMemo(() => computeLayout(lines, feeds), [lines, feeds])

  return (
    <div
      className="relative"
      style={{ width: layout.width, minWidth: layout.width, height: layout.height }}
    >
      <svg
        width={layout.width}
        height={layout.height}
        className="pointer-events-none absolute inset-0"
      >
        <defs>
          <marker
            id="site-feeds-arrow"
            viewBox="0 0 10 10"
            refX="9"
            refY="5"
            markerWidth="7"
            markerHeight="7"
            orient="auto-start-reverse"
          >
            <path d="M 0 0 L 10 5 L 0 10 z" fill="var(--ink-4)" />
          </marker>
        </defs>
        {feeds.map((edge, i) => {
          const a = layout.positions.get(edge.source)
          const b = layout.positions.get(edge.target)
          if (!a || !b) return null
          const x1 = a.x + NODE_W
          const y1 = a.y + NODE_H / 2
          const x2 = b.x
          const y2 = b.y + NODE_H / 2
          const mid = (x1 + x2) / 2
          const d = `M ${x1} ${y1} C ${mid} ${y1}, ${mid} ${y2}, ${x2 - 6} ${y2}`
          return (
            <path
              key={`${edge.source}->${edge.target}-${i}`}
              d={d}
              fill="none"
              stroke="var(--ink-4)"
              strokeWidth="1.5"
              opacity="0.7"
              markerEnd="url(#site-feeds-arrow)"
            />
          )
        })}
      </svg>
      {lines.map((line) => {
        const pos = layout.positions.get(line.id)
        if (!pos) return null
        return (
          <PlantHref
            key={line.id}
            id={line.id}
            label="Line"
            node={{ ...line, siteId }}
            className="absolute flex items-center gap-2 rounded-lg border bg-card px-3 py-2 text-card-foreground no-underline shadow-sm transition-colors hover:bg-accent hover:text-accent-foreground"
            style={{ left: pos.x, top: pos.y, width: NODE_W, height: NODE_H }}
          >
            <span className="grid size-7 place-items-center rounded-md bg-bg-sunk text-ink-3">
              <FlowArrowIcon size={14} />
            </span>
            <span className="min-w-0 flex-1">
              <span className="block truncate text-13 font-semibold text-ink">{line.name}</span>
              <span className="block truncate text-11 text-ink-3">
                {line.line_code ? `${line.line_code} · ` : ''}
                {line.equipmentCount} eq
              </span>
            </span>
          </PlantHref>
        )
      })}
    </div>
  )
}
